import { useState, useCallback } from 'react';
import axios from 'axios';
import logger from '../../utils/logger';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for fetching assessment results data
 */
export const useAssessmentData = (assessmentId) => {
  const [assessment, setAssessment] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAssessmentResults = useCallback(async () => {
    if (!assessmentId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Try new endpoint first (for completed_assessments)
      try {
        const response = await axios.get(
          `${backendUrl}/api/assessment/esa/results/${assessmentId}`,
          { withCredentials: true }
        );

        const assessmentData = response.data;
        setAssessment(assessmentData);
        
        logger.log('📊 Assessment data loaded:', {
          assessmentId: assessmentData?.assessment_id,
          facilityName: assessmentData?.facility_name,
          industryName: assessmentData?.industry_name,
          hasMetadata: !!assessmentData?.metadata,
          metadataKeys: assessmentData?.metadata ? Object.keys(assessmentData.metadata) : []
        });

        // Check if assessment metadata has recommendations
        if (assessmentData?.metadata?.recommendations && Array.isArray(assessmentData.metadata.recommendations)) {
          setRecommendations(assessmentData.metadata.recommendations);
          logger.log(`✅ Loaded ${assessmentData.metadata.recommendations.length} recommendations from assessment metadata`);
        } else {
          // If no recommendations in metadata, try to generate them from assessment data
          // This is a fallback when the generate-results endpoint isn't available
          logger.log('No recommendations in metadata, will try to generate from endpoint');
        }

        // Also try to fetch recommendations from generate-results endpoint (optional - don't block if it fails)
        // This provides enhanced recommendations with benchmarks
        // Run asynchronously so it doesn't block the main flow
        setTimeout(async () => {
          try {
            const resultsRes = await axios.post(
              `${backendUrl}/api/assessment/generate-results`,
              { assessment_id: assessmentId },
              { 
                withCredentials: true,
                timeout: 30000 // 30 second timeout
              }
            );

            if (resultsRes.data && resultsRes.data.success) {
              // Override with enhanced recommendations if available
              setRecommendations(resultsRes.data.recommendations || []);
              setBenchmarkData(resultsRes.data.benchmarks || {});
              logger.log(`✅ Loaded ${resultsRes.data.recommendations?.length || 0} enhanced recommendations`);
            }
          } catch (recErr) {
            // Log the error for debugging but don't block the UI
            if (recErr.response?.status === 404) {
              logger.warn('Generate-results endpoint not found (404). This is optional - using assessment data only.');
            } else if (recErr.response?.status === 500) {
              logger.warn('Generate-results endpoint error (500). This is optional - using assessment data only.');
            } else {
              logger.warn('Error fetching enhanced recommendations:', recErr.response?.status || recErr.message);
            }
            
            // Only set empty if we don't have recommendations from metadata
            // The assessment data should still display even without recommendations
            if (!assessmentData?.metadata?.recommendations) {
              // Don't set empty - let the UI show the assessment data with empty recommendations
              // The tabs will show appropriate empty states
              logger.log('No recommendations available from metadata or endpoint. Assessment data will still display.');
            }
          }
        }, 100); // Small delay to not block initial render

        setLoading(false);
        return;
      } catch (newErr) {
        logger.log('New endpoint not available, trying legacy endpoints');
      }

      // Fallback to old endpoints
      const [assessmentRes, resultsRes] = await Promise.all([
        axios.get(`${backendUrl}/api/assessment/routing-logic/${assessmentId}`),
        axios.post(`${backendUrl}/api/assessment/generate-results`, { assessment_id: assessmentId })
      ]);

      setAssessment(assessmentRes.data);

      if (resultsRes.data.success) {
        setRecommendations(resultsRes.data.recommendations || []);
        setBenchmarkData(resultsRes.data.benchmarks || {});
      }

      setLoading(false);
    } catch (err) {
      logger.error('Error fetching assessment results:', err);
      setError('Unable to load assessment results. Please try again.');
      setLoading(false);
    }
  }, [assessmentId]);

  return {
    assessment,
    recommendations,
    benchmarkData,
    loading,
    error,
    fetchAssessmentResults
  };
};

